/* MOTURO search normalisation and query engine.
   Pure functions only: no DOM, no storage, no network, no fitment inference.
   Shared by the theme-editor catalogue, the optional public catalogue index and the product-page vehicle list.
   Vehicle facets (make/model/year) come from the explicit source table columns only; free-text rows keep text search.
   Alias and synonym families are explicit, narrow and reversible. Distinct models are never merged. */
(function (root) {
  'use strict';
  const VERSION = '1.2.0';
  const MAX_QUERY_LENGTH = 200, MAX_QUERY_TOKENS = 12, MAX_NGRAM = 4;
  const SKU_PATTERN = /^[0-9]{4,12}$/;
  const DIGRAPHS = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' };

  /* Vehicle model alias families. Each member is a compact key (letters and digits only, separators removed).
     A key may belong to exactly one family; families are symmetric, so every resolution is reversible.
     Membership is limited to designations that name the same vehicle model. Series codes shared by several
     models (YFM 660 Raptor/Grizzly, XV/XVS, CBR 600 F/RR, GSX 1300 R/BK) are deliberately absent. */
  const ALIAS_FAMILIES = Object.freeze([
    { id: 'suzuki-hayabusa', members: ['hayabusa', 'gsx1300r', 'gsxr1300'], evidence: 'Owner-accepted pilot alias (screens/vehicle.js); title "GSX-R 1300 Hayabusa"; source row "Suzuki GSX 1300 R"' },
    { id: 'yamaha-banshee-350', members: ['banshee', 'yfz350'], evidence: 'Source row "Yamaha YFZ 350 Banshee"; titles "YFZ 350 Banshee", "YFZ350 Banshee"' },
    { id: 'yamaha-blaster-200', members: ['blaster', 'yfs200'], evidence: 'Titles "YFS 200 Blaster", "YFS200 Blaster"; source row "Yamaha YFS 200"' },
    { id: 'yamaha-warrior-350', members: ['warrior350', '350warrior', 'yfm350x'], evidence: 'Title "YFM 350X Warrior"; source row "Yamaha YFM 350 Warrior"' },
    { id: 'yamaha-rhino-660', members: ['rhino660', '660rhino', 'yxr660'], evidence: 'Title "Rhino YXR 660"; source row "Yamaha YXR 660 Rhino"' },
    { id: 'yamaha-rhino-450', members: ['rhino450', '450rhino', 'yxr450'], evidence: 'Title "Rhino 450 YXR 450"; source row "Yamaha YXR 450 Rhino"' },
    { id: 'yamaha-virago-250', members: ['virago250', '250virago', 'xv250'], evidence: 'Titles "XV 250 Virago", "Virago 250 3LS"; source rows "Yamaha XV 250 H/N/S" (XVS 250 excluded)' },
    { id: 'yamaha-virago-535', members: ['virago535', '535virago', 'xv535'], evidence: 'Title "XV 535 Virago XV 535"; source rows "Yamaha XV 535 …"' },
    { id: 'honda-hornet-600', members: ['hornet600', '600hornet', 'cb600f'], evidence: 'Title "CB600F Hornet 98-07"; source rows "Honda CB 600 F"' },
    { id: 'suzuki-bandit-250', members: ['bandit250', '250bandit', 'gsf250'], evidence: 'Title "Bandit GSF 250"' },
    { id: 'suzuki-bandit-400', members: ['bandit400', '400bandit', 'gsf400'], evidence: 'Source rows "Suzuki GSF 400"; GSF = Bandit series designation' },
    { id: 'suzuki-burgman-400', members: ['burgman400', '400burgman', 'burgmann400', '400burgmann', 'an400'], evidence: 'Titles "AN 400 Burgmann", "Burgmann AN 400"; source rows "Suzuki AN 400 A/S/Z"' },
    { id: 'yamaha-aerox-50', members: ['aerox50', '50aerox', 'nitro50', '50nitro', 'yq50'], evidence: 'Titles "MBK Nitro Yamaha Aerox 50"; source rows "Yamaha YQ 50 L", "MBK YQ 50 …" (YQ 100 excluded)' },
    { id: 'yamaha-majesty-125', members: ['majesty125', '125majesty', 'skyliner125', '125skyliner'], evidence: 'Titles "Skyliner Yamaha Majesty 125", "MBK Skyliner Majesty"; source rows "MBK YP 125 R"' },
    { id: 'yamaha-majesty-250', members: ['majesty250', '250majesty', 'yp250'], evidence: 'Title "Yamaha YP 250"; source rows "Yamaha YP 250 …"' },
    { id: 'yamaha-majesty-400', members: ['majesty400', '400majesty', 'yp400'], evidence: 'Title "Majesty 400 YP"' }
  ]);

  /* German part-name synonyms. Symmetric word sets; a word belongs to exactly one family. */
  const PART_SYNONYMS = Object.freeze([
    { id: 'cdi', members: ['cdi', 'zündbox'] },
    { id: 'regler', members: ['spannungsregler', 'gleichrichter', 'regler', 'laderegler'] },
    { id: 'lichtmaschine', members: ['lichtmaschine', 'lima', 'stator'] },
    { id: 'anlasser', members: ['anlasser', 'anlassermotor', 'startermotor', 'starter'] },
    { id: 'starterrelais', members: ['starterrelais', 'anlasserrelais', 'magnetschalter'] },
    { id: 'auspuff', members: ['auspuff', 'auspuffanlage', 'schalldämpfer'] },
    { id: 'bremsbelag', members: ['bremsbeläge', 'bremsbelag', 'bremsklötze', 'bremsklotz'] },
    { id: 'federbein', members: ['stoßdämpfer', 'federbein'] },
    { id: 'zylinderkit', members: ['zylinderkit', 'zylindersatz'] },
    { id: 'dichtsatz', members: ['dichtungssatz', 'dichtsatz'] },
    { id: 'wellendichtring', members: ['simmerring', 'simmering', 'wellendichtring'] },
    { id: 'benzinhahn', members: ['benzinhahn', 'kraftstoffhahn', 'tankhahn'] },
    { id: 'benzinpumpe', members: ['benzinpumpe', 'kraftstoffpumpe'] },
    { id: 'benzinfilter', members: ['benzinfilter', 'kraftstofffilter', 'spritfilter'] },
    { id: 'variorollen', members: ['variorollen', 'gewichtsrollen', 'variogewichte', 'variomatikrollen'] },
    { id: 'keilriemen', members: ['keilriemen', 'antriebsriemen', 'variomatikriemen'] },
    { id: 'drehzahlmesser', members: ['drehzahlmesser', 'dzm'] },
    { id: 'batterie', members: ['batterie', 'akku'] },
    { id: 'rücklicht', members: ['rücklicht', 'rückleuchte', 'heckleuchte'] },
    { id: 'blinker', members: ['blinker', 'blinkleuchte'] },
    { id: 'reparatursatz', members: ['reparatursatz', 'reparaturkit', 'repsatz', 'überholsatz'] },
    { id: 'ansaugstutzen', members: ['ansaugstutzen', 'ansaugkrümmer'] },
    { id: 'lenkerschalter', members: ['lenkerschalter', 'lenkerarmatur'] },
    { id: 'hitzeschutzband', members: ['hitzeschutzband', 'auspuffband', 'thermoband'] },
    { id: 'tankdeckel', members: ['tankdeckel', 'tankverschluss'] },
    { id: 'sitzbezug', members: ['sitzbezug', 'sitzbankbezug'] }
  ]);

  function text(value) { return value == null ? '' : String(value); }
  function digraph(value) { return text(value).normalize('NFC').toLowerCase().replace(/[äöüß]/g, c => DIGRAPHS[c]); }
  function fold(value) {
    return text(value).normalize('NFC').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');
  }
  function tokenize(value) {
    const out = [];
    for (const chunk of fold(value).split(/[^a-z0-9]+/)) {
      if (!chunk) continue;
      for (const part of chunk.match(/[a-z]+|[0-9]+/g) || []) out.push(part);
    }
    return out;
  }
  function compact(tokens) { return tokens.join(''); }
  function isNumeric(token) { return /^[0-9]+$/.test(token); }
  function isSku(value) { return SKU_PATTERN.test(text(value)); }

  const familyByKey = new Map(), synonymByWord = new Map();
  for (const family of ALIAS_FAMILIES) {
    for (const member of family.members) {
      if (!/^[a-z0-9]+$/.test(member)) throw new Error('Alias key must be compact: ' + member);
      if (familyByKey.has(member)) throw new Error('Alias key in two families: ' + member);
      familyByKey.set(member, family.id);
    }
  }
  for (const family of PART_SYNONYMS) {
    const forms = new Set();
    for (const member of family.members) for (const form of [fold(member), fold(digraph(member))]) forms.add(form);
    for (const form of forms) {
      if (!/^[a-z]+$/.test(form)) throw new Error('Synonym must be a single German word: ' + form);
      if (synonymByWord.has(form)) throw new Error('Synonym in two families: ' + form);
      synonymByWord.set(form, [...forms]);
    }
  }
  function familyOf(key) { return familyByKey.get(compact(tokenize(key))) || null; }
  function synonymsOf(word) { return synonymByWord.get(fold(word)) || []; }

  const runCache = new Map();
  function runPattern(key) {
    let re = runCache.get(key);
    if (!re) { re = new RegExp('\\|' + key.split('').join('\\|?') + '\\|'); runCache.set(key, re); }
    return re;
  }
  function hasRun(doc, key) { return doc.compact.includes(key) && runPattern(key).test(doc.joined); }

  function fragment(strings) {
    const tokens = [], tokenSet = new Set();
    for (const value of strings) {
      for (const token of tokenize(value)) { tokens.push(token); tokenSet.add(token); }
      const alt = digraph(value);
      if (alt !== text(value).normalize('NFC').toLowerCase()) for (const token of tokenize(alt)) tokenSet.add(token);
    }
    const doc = { tokens, tokenSet, joined: '|' + tokens.join('|') + '|', compact: compact(tokens), families: new Set() };
    for (const [key, id] of familyByKey) if (hasRun(doc, key)) doc.families.add(id);
    return doc;
  }

  /* items: any objects. accessors: key(item), sku(item) -> string or '', primary(item) -> strings, secondary(item) -> strings.
     mode 'product' enables German part synonyms and SKU rules; mode 'vehicle' matches labels only. */
  function createIndex(items, accessors) {
    const a = Object.assign({ key: it => it.id, sku: () => '', primary: it => [it.label || it.title || ''], secondary: () => [], mode: 'product' }, accessors || {});
    const docs = [], skuSet = new Set();
    for (const item of items || []) {
      const sku = text(a.sku(item)), primary = a.primary(item).map(text), secondary = a.secondary(item).map(text);
      const doc = { key: a.key(item), item, sku, primary: fragment(primary), all: fragment(primary.concat(secondary, sku ? [sku] : [])) };
      docs.push(doc);
      if (sku) skuSet.add(sku);
    }
    return { docs, skuSet, mode: a.mode === 'vehicle' ? 'vehicle' : 'product', size: docs.length };
  }

  function parseQuery(query) {
    return tokenize(text(query).slice(0, MAX_QUERY_LENGTH)).slice(0, MAX_QUERY_TOKENS);
  }

  function wordMatch(part, term) {
    if (part.tokenSet.has(term)) return true;
    if (term.length >= 3) for (const token of part.tokenSet) if (token.startsWith(term)) return true;
    if (term.length >= 4) for (const token of part.tokenSet) if (token.includes(term)) return true;
    return false;
  }
  function numberMatch(part, term) {
    if (part.tokenSet.has(term)) return true;
    return false;
  }
  function unitMatch(part, term) {
    // "50" matches "50ccm"; digits followed only by letters, never another number.
    for (const token of part.tokenSet) if (token.length > term.length && token.startsWith(term) && /^[a-z]+$/.test(token.slice(term.length))) return true;
    return false;
  }

  function matchPart(doc, part, terms, index, useSynonyms) {
    const kinds = [];
    let i = 0;
    while (i < terms.length) {
      let consumed = 0;
      for (let n = Math.min(MAX_NGRAM, terms.length - i); n >= 2 && !consumed; n--) {
        const key = compact(terms.slice(i, i + n));
        const family = familyByKey.get(key);
        if (family && part.families.has(family)) { kinds.push('alias'); consumed = n; }
        else if (hasRun(part, key)) { kinds.push('model'); consumed = n; }
      }
      if (consumed) { i += consumed; continue; }
      const term = terms[i];
      const family = familyByKey.get(term);
      if (family && part.families.has(family)) kinds.push('alias');
      else if (isNumeric(term)) {
        if (index.mode === 'product' && doc.sku && doc.sku === term) kinds.push('sku_exact');
        else if (numberMatch(part, term)) kinds.push('number');
        else if (unitMatch(part, term)) kinds.push('number');
        else if (hasRun(part, term)) kinds.push('model');
        else return null;
      } else if (hasRun(part, term) || wordMatch(part, term)) kinds.push('word');
      else if (useSynonyms && synonymsOf(term).some(form => wordMatch(part, form))) kinds.push('synonym');
      else return null;
      i += 1;
    }
    return kinds;
  }

  const WEIGHT = { sku_exact: 100, alias: 12, model: 12, word: 10, number: 6, synonym: 5 };
  function score(kinds, weight) { return kinds.reduce((sum, kind) => sum + (WEIGHT[kind] || 1) * weight, 0); }

  /* Returns every matching item ranked; empty query returns every item in index order. */
  function query(index, q, options) {
    const terms = parseQuery(q), limit = options && options.limit, useSynonyms = index.mode === 'product';
    // Exact identifiers override textual occurrences in unrelated articles.
    if (index.mode === 'product' && terms.length === 1 && index.skuSet.has(terms[0])) {
      const exact = index.docs.filter(doc => doc.sku === terms[0]).map(doc => ({item: doc.item, key: doc.key, score: 400, kinds: ['sku_exact']}));
      return {terms, total: exact.length, results: limit ? exact.slice(0, limit) : exact};
    }
    const results = [];
    for (const doc of index.docs) {
      if (!terms.length) { results.push({ item: doc.item, key: doc.key, score: 0, kinds: [] }); continue; }
      const kinds = matchPart(doc, doc.all, terms, index, useSynonyms);
      if (!kinds) continue;
      const primaryKinds = matchPart(doc, doc.primary, terms, index, useSynonyms) || [];
      results.push({ item: doc.item, key: doc.key, score: score(kinds, 1) + score(primaryKinds, 3), kinds });
    }
    if (terms.length) results.sort((x, y) => y.score - x.score || String(x.key).localeCompare(String(y.key), 'de'));
    return { terms, total: results.length, results: limit ? results.slice(0, limit) : results };
  }

  /* ---- Vehicle facets from the explicit source table columns ----
     Editor and public vehicle labels are the non-empty source table cells joined by ' · '
     (build_editor_catalogue.py); cells never contain the separator, so the split is lossless.
     Structured rows carry [designation, years, power, displacement]; the years cell is 'YYYY', 'YYYY-YYYY'
     or '0' (unknown). Any other shape is free text: text search only, no facets, no invented metadata.
     The make is the leading word of the designation column; multi-word makes are listed with evidence.
     Facets filter source rows. They never build a model-year identity or a fitment statement. */
  const LABEL_SEPARATOR = ' · ';
  const YEAR_CELL = /^([0-9]{4})(?:-([0-9]{4}))?$/;
  const YEAR_MIN = 1900, YEAR_MAX = 2100, YEAR_SPAN_MAX = 60;
  const MULTI_WORD_MAKES = Object.freeze([
    { make: 'Harley Davidson', evidence: 'Source rows "Harley Davidson FLHR 1450", "Harley Davidson FLHTC 1340" (17 designations)' },
    { make: 'China Scooter', evidence: 'Source rows "China Scooter 152QMI GY6 125 4T", "China Scooter BT49QT-10 50 4T" (87 designations; source wording kept verbatim)' },
    { make: 'Arctic Cat', evidence: 'Source rows "Arctic Cat DVX 400", "Arctic Cat Cat 400"' },
    { make: 'Flex Tech', evidence: 'Source rows "Flex Tech Fun 50 4T", "Flex Tech Venus 50 4T"' }
  ]);
  function parseYears(cell) {
    const m = YEAR_CELL.exec(cell);
    if (!m) return null;
    const from = Number(m[1]), to = m[2] ? Number(m[2]) : from;
    if (from < YEAR_MIN || to > YEAR_MAX || to < from || to - from > YEAR_SPAN_MAX) return null;
    return { from, to };
  }
  function vehicleFacets(label) {
    const value = text(label);
    const base = { label: value, structured: false, designation: value, make: null, model: null, years: null, yearsText: '', qualifiers: [] };
    const parts = value.split(LABEL_SEPARATOR);
    if (parts.length < 2 || !parts[0].trim()) return base;
    const yearsCell = parts[1].trim();
    if (yearsCell !== '0' && !YEAR_CELL.test(yearsCell)) return base;
    const designation = parts[0].trim();
    const multi = MULTI_WORD_MAKES.find(m => designation === m.make || designation.startsWith(m.make + ' '));
    const make = multi ? multi.make : designation.split(' ')[0];
    const model = designation.slice(make.length).trim() || designation;
    return { label: value, structured: true, designation, make, model, years: parseYears(yearsCell),
      yearsText: yearsCell === '0' ? '' : yearsCell, qualifiers: parts.slice(2).map(p => p.trim()).filter(Boolean) };
  }
  const sortDe = (a, b) => String(a).localeCompare(String(b), 'de');
  function createVehicleFacetIndex(vehicles) {
    const facets = new Map(), makes = new Map();
    let unstructured = 0;
    for (const v of vehicles || []) {
      const f = vehicleFacets(v.label);
      facets.set(v.id, f);
      if (!f.structured) { unstructured += 1; continue; }
      if (!makes.has(f.make)) makes.set(f.make, new Map());
      const models = makes.get(f.make);
      if (!models.has(f.model)) models.set(f.model, []);
      models.get(f.model).push(v);
    }
    const rowsOf = (make, model) => {
      const models = makes.get(text(make));
      if (!models) return [];
      if (text(model)) return models.get(text(model)) || [];
      return [...models.values()].flat();
    };
    return {
      facets, size: facets.size, unstructured,
      makeList: [...makes.keys()].sort(sortDe),
      modelsOf: make => makes.has(text(make)) ? [...makes.get(text(make)).keys()].sort(sortDe) : [],
      /* Years stated by the source ranges of the given make/model; a filter list, never a model-year identity. */
      yearsOf(make, model) {
        const years = new Set();
        let withoutYear = 0;
        for (const v of rowsOf(make, model)) {
          const f = facets.get(v.id);
          if (!f.years) { withoutYear += 1; continue; }
          for (let y = f.years.from; y <= f.years.to; y++) years.add(y);
        }
        return { years: [...years].sort((a, b) => a - b), withoutYear };
      }
    };
  }
  /* selection: {make, model, year} strings from the controls. Unknown values match nothing (fail closed).
     A year matches only rows whose stated range contains it; rows of the same make/model without a stated
     year are reported separately in withoutYear and never counted as matches. */
  function filterVehicles(index, vehicles, selection) {
    const make = text(selection && selection.make).trim(), model = text(selection && selection.model).trim();
    const yearText = text(selection && selection.year).trim();
    const year = /^[0-9]{4}$/.test(yearText) ? Number(yearText) : null;
    const yearActive = yearText !== '', facetsActive = !!(make || model || yearActive);
    const out = { matches: [], withoutYear: [], unstructuredHidden: 0, invalidYear: yearActive && year === null };
    for (const v of vehicles || []) {
      const f = index.facets.get(v.id) || vehicleFacets(v.label);
      if (!facetsActive) { out.matches.push(v); continue; }
      if (!f.structured) { out.unstructuredHidden += 1; continue; }
      if (make && f.make !== make) continue;
      if (model && f.model !== model) continue;
      if (!yearActive) { out.matches.push(v); continue; }
      if (year === null) continue;
      if (f.years && f.years.from <= year && year <= f.years.to) out.matches.push(v);
      else if (!f.years) out.withoutYear.push(v);
    }
    return out;
  }
  /* Finder step resolution for Hersteller/Modell/Baujahr (1.2.0). Pure and fail-closed: decides whether exactly one
     structured source row remains for make + model + EXACT year. 'unique' needs one match, no same-model row without a
     stated year and, when options.textIndex is given, no free-text row that names the designation. Several remaining
     rows are never merged into one vehicle; they are returned for a manual choice with the columns in which they differ. */
  function resolveVehicleChoice(index, vehicles, selection, options) {
    const str = v => typeof v === 'string' ? v.trim() : ''; // non-string control values never resolve
    const make = str(selection && selection.make), model = str(selection && selection.model), year = str(selection && selection.year);
    const base = { state: 'none', step: 1, make, model, year, matches: [], withoutYear: [], competingText: 0, unique: null, differences: [] };
    if (!make) return base;
    const filtered = filterVehicles(index, vehicles, { make, model, year });
    base.matches = filtered.matches; base.withoutYear = filtered.withoutYear;
    if (!model) return Object.assign(base, { state: 'partial', step: 2 });
    if (!year) return Object.assign(base, index.yearsOf(make, model).years.length ? { state: 'partial', step: 3 } : { state: 'choose', step: 4 });
    if (filtered.invalidYear) return Object.assign(base, { state: 'invalid', step: 3 });
    const textIndex = options && options.textIndex;
    if (textIndex) {
      const designation = make === model ? make : make + ' ' + model;
      base.competingText = query(textIndex, designation).results.filter(r => !(index.facets.get(r.item.id) || vehicleFacets(r.item.label)).structured).length;
    }
    const facetsOf = filtered.matches.map(v => index.facets.get(v.id) || vehicleFacets(v.label));
    if (new Set(facetsOf.map(f => f.yearsText)).size > 1) base.differences.push('years');
    if (new Set(facetsOf.map(f => f.qualifiers.join('|'))).size > 1) base.differences.push('qualifiers');
    if (filtered.matches.length === 1 && !filtered.withoutYear.length && !base.competingText) return Object.assign(base, { state: 'unique', step: 4, unique: filtered.matches[0] });
    return Object.assign(base, { state: 'choose', step: 4 });
  }
  /* Source relations of exactly one article number: verbatim rows, no title inference, no fitment status. */
  function relationsFor(vehicles, sku, index) {
    const key = text(sku).trim();
    if (!isSku(key)) return [];
    const out = [];
    for (const v of vehicles || []) {
      if (!Array.isArray(v.skus) || !v.skus.includes(key)) continue;
      out.push({ id: v.id, label: v.label, facets: (index && index.facets.get(v.id)) || vehicleFacets(v.label) });
    }
    return out;
  }
  function paginate(items, page, size) {
    const list = items || [], per = Math.max(1, Math.floor(Number(size)) || 1), total = list.length;
    const pages = Math.max(1, Math.ceil(total / per));
    const current = Math.min(Math.max(1, Math.floor(Number(page)) || 1), pages);
    return { page: current, pages, total, items: list.slice((current - 1) * per, current * per) };
  }

  const api = Object.freeze({
    VERSION, fold, digraph, tokenize, compact, isSku, isNumeric, parseQuery, familyOf, synonymsOf,
    families: ALIAS_FAMILIES, synonyms: PART_SYNONYMS, createIndex, query,
    LABEL_SEPARATOR, multiWordMakes: MULTI_WORD_MAKES, parseYears, vehicleFacets, createVehicleFacetIndex, filterVehicles, resolveVehicleChoice, relationsFor, paginate
  });
  root.MoturoSearch = api;
  if (typeof module !== 'undefined') module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
